import React, { useState } from 'react';
import { Form, DatePicker, Select } from 'antd';
import { Link, useNavigate } from 'react-router-dom';
import Auth from '../../../../service/Auth';
import { useNotify, useWithNotify } from '../../../../providers/NotificationProvider';
import { signIn } from 'aws-amplify/auth';
import dayjs from 'dayjs';
import Logo from '../../../../assets/img/LogoÑuñoa.png';
import { FiUser, FiMail, FiLock, FiPhone, FiHome, FiCreditCard, FiEye, FiEyeOff, FiArrowRight, FiCheck } from 'react-icons/fi';

const { Option } = Select;

/* ── Campo reutilizable ── */
const Field = ({ label, error, required, children, hint }) => (
  <div className="flex flex-col gap-1.5">
    <label className="text-[13px] font-bold text-gray-700">
      {label} {required && <span className="text-red-400">*</span>}
    </label>
    {children}
    {hint && <p className="text-[11px] text-gray-400">{hint}</p>}
    {error && <p className="text-[12px] text-red-500 font-medium">{error}</p>}
  </div>
);

const Input = ({ icon, error, ...props }) => (
  <div className={`flex items-center gap-2 bg-white rounded-2xl border px-4 py-3 transition-all
    ${error ? 'border-red-300 ring-2 ring-red-100' : 'border-gray-200 focus-within:border-blue-400 focus-within:ring-2 focus-within:ring-blue-100'}`}>
    {icon && <span className="text-gray-400 flex-shrink-0">{icon}</span>}
    <input className="flex-1 bg-transparent text-sm text-gray-900 outline-none placeholder-gray-400" {...props} />
  </div>
);

/* ── Helpers RUT ── */
const fmtRut = (v = '') => {
  const c = v.replace(/\./g, '').replace(/-/g, '').replace(/\s/g, '').toUpperCase().replace(/[^0-9K]/g, '');
  if (c.length <= 1) return c;
  return `${c.slice(0, -1)}-${c.slice(-1)}`;
};
const validRut = (v = '') => {
  const c = v.replace(/\./g, '').replace(/-/g, '').toUpperCase();
  if (!/^[0-9]+[0-9K]$/.test(c)) return false;
  const body = c.slice(0, -1), dv = c.slice(-1);
  let s = 0, m = 2;
  for (let i = body.length - 1; i >= 0; i--) { s += parseInt(body[i]) * m; m = m === 7 ? 2 : m + 1; }
  const r = 11 - (s % 11);
  return (r === 11 ? '0' : r === 10 ? 'K' : String(r)) === dv;
};
const normalPhone = (v = '') => {
  const d = v.replace(/\D/g, '');
  return d.startsWith('56') ? `+${d}` : `+56${d.replace(/^0/, '')}`;
};

const COMUNAS = [
  "Cerrillos", "Cerro Navia", "Conchalí", "El Bosque", "Estación Central", "Huechuraba",
  "Independencia", "La Cisterna", "La Florida", "La Granja", "La Pintana", "La Reina",
  "Las Condes", "Lo Barnechea", "Lo Espejo", "Lo Prado", "Macul", "Maipú", "Ñuñoa",
  "Pedro Aguirre Cerda", "Peñalolén", "Providencia", "Pudahuel", "Quilicura", "Quinta Normal",
  "Recoleta", "Renca", "San Joaquín", "San Miguel", "San Ramón", "Santiago", "Vitacura",
  "Pirque", "Puente Alto", "San José de Maipo", "Colina", "Lampa", "Tiltil",
  "Buin", "Calera de Tango", "Paine", "San Bernardo",
  "Alhué", "Curacaví", "María Pinto", "Melipilla", "San Pedro",
  "El Monte", "Isla de Maipo", "Padre Hurtado", "Peñaflor", "Talagante"
];

const PASSWORD_RE = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[ !"#$%&'()*+,\-./:;<=>?@[\\\]^_`{|}~]).{8,}$/;

const STEPS = ['Cuenta', 'Personal', 'Ubicación'];

export default function RegisterForm() {
  const [form] = Form.useForm();
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [showPwd, setShowPwd] = useState(false);
  const [showPwd2, setShowPwd2] = useState(false);
  const [errors, setErrors] = useState({});
  const [vals, setVals] = useState({
    name: '', email: '', password: '', confirm: '',
    phone_number: '', birthdate: null, rut: '',
    address: '', commune: '', gender: '', enfermedadesBases: ''
  });
  const navigate = useNavigate();
  const notify = useNotify();
  const withNotify = useWithNotify();

  const set = (k, v) => setVals(p => ({ ...p, [k]: v }));

  /* ── Validaciones por paso ── */
  const validate = (s) => {
    const e = {};
    if (s === 0) {
      if (!vals.name.trim()) e.name = 'El nombre es requerido';
      else if (!/^[a-zA-ZáéíóúÁÉÍÓÚñÑüÜ\s]+$/.test(vals.name)) e.name = 'Solo letras y espacios';
      if (!vals.email.trim()) e.email = 'El email es requerido';
      else if (!/^[a-zA-Z0-9._+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(vals.email)) e.email = 'Formato inválido (sin tildes ni ñ)';
      if (!vals.password) e.password = 'La contraseña es requerida';
      else if (!PASSWORD_RE.test(vals.password)) e.password = 'Mínimo 8 caracteres, mayúscula, número y símbolo';
      if (vals.password !== vals.confirm) e.confirm = 'Las contraseñas no coinciden';
    }
    if (s === 1) {
      if (!vals.phone_number) e.phone_number = 'El teléfono es requerido';
      else if (!/^\+56[9]\d{8}$/.test(normalPhone(vals.phone_number))) e.phone_number = 'Formato: +56912345678';
      if (!vals.birthdate) e.birthdate = 'La fecha de nacimiento es requerida';
      if (!vals.rut) e.rut = 'El RUT es requerido';
      else { const r = fmtRut(vals.rut); if (!validRut(r)) e.rut = 'RUT no válido'; }
      if (!vals.gender) e.gender = 'Selecciona tu género';
    }
    if (s === 2) {
      if (!vals.address.trim()) e.address = 'La dirección es requerida';
      if (!vals.commune) e.commune = 'La comuna es requerida';
    }
    return e;
  };

  const handleNext = () => {
    const e = validate(step);
    setErrors(e);
    if (!Object.keys(e).length) setStep(s => s + 1);
  };

  const handleSubmit = async () => {
    const e = validate(2);
    setErrors(e);
    if (Object.keys(e).length) return;
    setSubmitting(true);
    try {
      const email = vals.email.trim().toLowerCase();
      const birthdate = vals.birthdate ? dayjs(vals.birthdate).format('YYYY-MM-DD') : undefined;
      const phone = normalPhone(vals.phone_number);
      const rut = fmtRut(vals.rut);

      await withNotify(
        Auth.register(email, vals.password, vals.name.trim(), phone, birthdate, vals.address.trim(), rut, vals.gender, vals.commune, vals.enfermedadesBases),
        { loading: 'Creando cuenta…', success: 'Cuenta creada. Revisa tu email para confirmar.', error: 'Error al crear la cuenta.' }
      );

      const age = birthdate ? dayjs().diff(dayjs(birthdate), 'year') : 0;
      if (age >= 60) {
        // Adulto mayor: auto-signIn sin confirmación por email
        try {
          await signIn({ username: email, password: vals.password });
          setTimeout(() => {
            try {
              navigate('/perfil');
              window.location.reload();
            } catch (navErr) {
              console.error('Error navegando tras registro:', navErr);
              window.location.href = '/perfil';
            }
          }, 300);
        } catch (signInErr) {
          console.error('Error auto-signIn tras registro de adulto mayor:', signInErr);
          notify.warning('Cuenta creada. Por favor inicia sesión manualmente.');
          navigate('/login');
        }
      } else {
        try {
          navigate('/confirmar-cuenta', { state: { email, savedPassword: vals.password } });
        } catch (navErr) {
          console.error('Error navegando a confirmar-cuenta:', navErr);
          window.location.href = '/confirmar-cuenta';
        }
      }
    } catch (err) {
      console.error('Error en registro:', err);
      notify.error(err?.message || 'Error inesperado. Intenta nuevamente.');
    } finally { setSubmitting(false); }
  };

  /* ── Render por paso ── */
  const Step0 = () => (
    <div className="space-y-4">
      <Field label="Nombre completo" required error={errors.name}>
        <Input icon={<FiUser size={16} />} placeholder="María González" value={vals.name}
          onChange={e => set('name', e.target.value)} error={errors.name} />
      </Field>
      <Field label="Correo electrónico" required error={errors.email}>
        <Input icon={<FiMail size={16} />} type="email" placeholder="tu@email.com" value={vals.email}
          onChange={e => set('email', e.target.value)} error={errors.email} />
      </Field>
      <Field label="Contraseña" required error={errors.password}
        hint="Mínimo 8 caracteres · mayúscula · número · símbolo">
        <div className={`flex items-center gap-2 bg-white rounded-2xl border px-4 py-3 transition-all
          ${errors.password ? 'border-red-300 ring-2 ring-red-100' : 'border-gray-200 focus-within:border-blue-400 focus-within:ring-2 focus-within:ring-blue-100'}`}>
          <FiLock size={16} className="text-gray-400 flex-shrink-0" />
          <input type={showPwd ? 'text' : 'password'} placeholder="••••••••" value={vals.password}
            onChange={e => set('password', e.target.value)}
            className="flex-1 bg-transparent text-sm text-gray-900 outline-none placeholder-gray-400" />
          <button type="button" onClick={() => setShowPwd(!showPwd)} className="text-gray-400">
            {showPwd ? <FiEyeOff size={16} /> : <FiEye size={16} />}
          </button>
        </div>
      </Field>
      <Field label="Confirmar contraseña" required error={errors.confirm}>
        <div className={`flex items-center gap-2 bg-white rounded-2xl border px-4 py-3 transition-all
          ${errors.confirm ? 'border-red-300 ring-2 ring-red-100' : 'border-gray-200 focus-within:border-blue-400 focus-within:ring-2 focus-within:ring-blue-100'}`}>
          <FiLock size={16} className="text-gray-400 flex-shrink-0" />
          <input type={showPwd2 ? 'text' : 'password'} placeholder="••••••••" value={vals.confirm}
            onChange={e => set('confirm', e.target.value)}
            className="flex-1 bg-transparent text-sm text-gray-900 outline-none placeholder-gray-400" />
          <button type="button" onClick={() => setShowPwd2(!showPwd2)} className="text-gray-400">
            {showPwd2 ? <FiEyeOff size={16} /> : <FiEye size={16} />}
          </button>
        </div>
      </Field>
    </div>
  );

  const Step1 = () => (
    <div className="space-y-4">
      <Field label="Teléfono" required error={errors.phone_number} hint="+56 9 1234 5678">
        <Input icon={<FiPhone size={16} />} placeholder="+56 9 1234 5678" value={vals.phone_number}
          maxLength={12} onChange={e => set('phone_number', e.target.value)} error={errors.phone_number} />
      </Field>
      <Field label="Fecha de nacimiento" required error={errors.birthdate}>
        <DatePicker size="large" className="w-full" format="DD/MM/YYYY"
          value={vals.birthdate} onChange={d => set('birthdate', d)}
          disabledDate={c => c && c > dayjs().endOf('day')}
          style={{ borderRadius: 16, borderColor: errors.birthdate ? '#fca5a5' : '#e5e7eb' }}
        />
      </Field>
      <Field label="RUT" required error={errors.rut}>
        <Input icon={<FiCreditCard size={16} />} placeholder="20931242-5" value={vals.rut}
          onChange={e => set('rut', fmtRut(e.target.value))} error={errors.rut} />
      </Field>
      <Field label="Género" required error={errors.gender}>
        <Select size="large" placeholder="Selecciona" value={vals.gender || undefined}
          onChange={v => set('gender', v)} className="w-full"
          style={{ borderRadius: 16 }}>
          <Option value="masculino">Masculino</Option>
          <Option value="femenino">Femenino</Option>
          <Option value="otro">Otro</Option>
        </Select>
      </Field>
      <Field label="Enfermedades o condiciones base" error={errors.enfermedadesBases}>
        <textarea value={vals.enfermedadesBases} onChange={e => set('enfermedadesBases', e.target.value)}
          rows={2} maxLength={500} placeholder="Opcional — indica condiciones de salud relevantes"
          className="w-full bg-white rounded-2xl border border-gray-200 px-4 py-3 text-sm text-gray-900 outline-none placeholder-gray-400 resize-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100 transition-all"
        />
      </Field>
    </div>
  );

  const Step2 = () => (
    <div className="space-y-4">
      <Field label="Dirección" required error={errors.address}>
        <Input icon={<FiHome size={16} />} placeholder="Calle y número" value={vals.address}
          onChange={e => set('address', e.target.value)} error={errors.address} />
      </Field>
      <Field label="Comuna" required error={errors.commune}>
        <Select size="large" showSearch placeholder="Busca tu comuna" value={vals.commune || undefined}
          onChange={v => set('commune', v)} className="w-full"
          filterOption={(input, option) => option.children.toLowerCase().includes(input.toLowerCase())}>
          {COMUNAS.map(c => <Option key={c} value={c}>{c}</Option>)}
        </Select>
      </Field>
    </div>
  );

  return (
    <div className="min-h-screen flex" style={{ background: '#f4f6f9' }}>

      {/* ── Formulario ── */}
      <div className="flex-1 flex flex-col justify-center px-6 py-10 sm:px-10 lg:px-16 max-w-lg w-full mx-auto lg:mx-0">

        <Link to="/" className="mb-8 inline-block">
          <img src={Logo} alt="Ñuñoa Deportes" className="h-9 w-auto" />
        </Link>

        <div className="mb-6">
          <h1 className="text-3xl font-extrabold text-gray-900 mb-1">Crear cuenta</h1>
          <p className="text-gray-500 text-sm">Regístrate gratis y accede a todos los talleres</p>
        </div>

        {/* Step indicator */}
        <div className="flex items-center gap-2 mb-7">
          {STEPS.map((s, i) => (
            <React.Fragment key={s}>
              <div className="flex items-center gap-1.5">
                <div className={`w-7 h-7 rounded-full flex items-center justify-center text-[12px] font-bold transition-all
                  ${i < step ? 'bg-blue-500 text-white' : i === step ? 'text-white' : 'bg-gray-200 text-gray-400'}`}
                  style={i === step ? { background: '#0047bb' } : {}}>
                  {i < step ? <FiCheck size={13} strokeWidth={3} /> : i + 1}
                </div>
                <span className={`text-[12px] font-semibold hidden sm:block ${i === step ? 'text-gray-900' : i < step ? 'text-blue-600' : 'text-gray-400'}`}>
                  {s}
                </span>
              </div>
              {i < STEPS.length - 1 && (
                <div className={`flex-1 h-0.5 rounded-full ${i < step ? 'bg-blue-400' : 'bg-gray-200'}`} />
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Paso actual */}
        <div className="nd-card p-6 mb-5">
          {step === 0 && Step0()}
          {step === 1 && Step1()}
          {step === 2 && Step2()}
        </div>

        {/* Botones de navegación */}
        <div className="flex gap-3">
          {step > 0 && (
            <button onClick={() => setStep(s => s - 1)}
              className="flex-1 py-3.5 rounded-2xl border-2 border-gray-200 text-sm font-bold text-gray-700 hover:bg-gray-50 transition-colors">
              ← Atrás
            </button>
          )}
          {step < 2 ? (
            <button onClick={handleNext}
              className="flex-1 flex items-center justify-center gap-2 py-3.5 rounded-2xl text-sm font-bold text-white transition-all hover:brightness-105 active:scale-[.98] shadow-md"
              style={{ background: 'linear-gradient(135deg,#003a94,#0047bb)' }}>
              Continuar <FiArrowRight size={16} />
            </button>
          ) : (
            <button onClick={handleSubmit} disabled={submitting}
              className="flex-1 flex items-center justify-center gap-2 py-3.5 rounded-2xl text-sm font-bold text-white transition-all disabled:opacity-60 hover:brightness-105 active:scale-[.98] shadow-md"
              style={{ background: 'linear-gradient(135deg,#003a94,#0047bb)' }}>
              {submitting
                ? <><span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> Creando cuenta…</>
                : <><FiCheck size={16} /> Crear cuenta</>
              }
            </button>
          )}
        </div>

        <p className="mt-6 text-center text-sm text-gray-500">
          ¿Ya tienes cuenta?{' '}
          <Link to="/login" className="font-bold text-blue-600 hover:text-blue-700">Ingresar</Link>
        </p>
      </div>

      {/* ── Panel derecho — solo lg ── */}
      <div className="hidden lg:flex flex-1 relative overflow-hidden">
        {/* Imagen de fondo */}
        <img
          src="https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?auto=format&fit=crop&w=1280&q=80"
          alt="" aria-hidden
          className="absolute inset-0 w-full h-full object-cover object-center"
        />
        {/* Overlay */}
        <div className="absolute inset-0" style={{
          background: 'linear-gradient(170deg, rgba(8,18,32,.88) 0%, rgba(0,50,120,.72) 55%, rgba(28,90,200,.50) 100%)'
        }} />

        {/* Contenido */}
        <div className="relative z-10 flex flex-col justify-between h-full p-12">
          {/* Badge superior */}
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-4 py-2 w-fit">
            <span className="w-2 h-2 rounded-full bg-blue-400 animate-pulse" />
            <span className="text-white/90 text-xs font-semibold tracking-wide">Ñuñoa Deportes — Registro</span>
          </div>

          {/* Texto principal */}
          <div>
            <h2 className="text-white font-black text-4xl leading-tight mb-4 drop-shadow">
              Únete a la<br />comunidad activa.
            </h2>
            <p className="text-white/70 text-base leading-relaxed max-w-xs mb-8">
              Talleres para todas las edades y niveles. Con tu RUT Vecino, accede a precios especiales desde el primer día.
            </p>

            {/* Beneficios */}
            <div className="space-y-3">
              {[
                'Registro gratuito en 2 minutos',
                'Precios preferenciales para vecinos',
                'Inscripción 100% online, sin filas',
                'Talleres para niños, adultos y adultos mayores',
              ].map(b => (
                <div key={b} className="flex items-center gap-3 text-white/85 text-sm">
                  <span className="w-5 h-5 rounded-full bg-white/15 flex items-center justify-center flex-shrink-0">
                    <FiCheck size={11} className="text-blue-400" strokeWidth={3} />
                  </span>
                  {b}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
